import memoize from 'lodash/memoize';
import { VirtualChallengeType } from '@letsdothis/reference-data';

import { breakpointLGInt } from '../../base-styles/vars';
import {
  BLACKLISTED_EO_IDS,
  BLACKLISTED_EVENT_IDS,
  BUYER_NOT_PARTICIPANT_FIELDS,
  GROUP_EMERGENCY_FIELDS,
  IS_PARTICIPANT_FIELD_ID,
  ORGANISER_OPT_IN,
  PARTICIPANT_EMAIL_FIELD_ID,
  PARTICIPANT_FIRST_NAME_FIELD_ID,
  PARTICIPANT_PHONE_FIELD_ID,
  PARTICIPANT_SURNAME_FIELD_ID,
  TICKET_BUYER_FIELDS,
  TICKET_BUYER_FIRST_NAME_FIELD_ID,
  TICKET_BUYER_SURNAME_FIELD_ID,
} from './constants';

// TODO: These should probably be split into separate files under the utils directory.

// this filter IN all the fields that have a particular type
export const filterOutFormInputs = (field) => {
  return field.type !== 'HEADING';
};
/**
 * Filters fields to return participant only fields (as opposed to group fields)
 * @param {} fields
 */
export const getParticipantFields = (fields) => {
  const fieldsBlacklist = new Set([
    ...TICKET_BUYER_FIELDS,
    ...GROUP_EMERGENCY_FIELDS,
    ORGANISER_OPT_IN,
  ]);
  return fields
    .filter((field) => !fieldsBlacklist.has(field.id))
    .filter(filterOutFormInputs);
};
/**
 * Filters fields to return fields shared by a group.
 * @param {} fields
 */
export const getGroupFields = (fields) => {
  // IDs of fields generated from template
  const groupFieldSet = new Set(GROUP_EMERGENCY_FIELDS);

  return fields.filter((field) => groupFieldSet.has(field.id));
};

export const defaultValueInGroupBookingFormForField = (fieldId, fields) => {
  if (fieldId === TICKET_BUYER_FIRST_NAME_FIELD_ID) {
    const matches = fields.filter(
      (f) => f.id === TICKET_BUYER_FIRST_NAME_FIELD_ID,
    );
    if (matches && matches.length > 0 && matches[0].defaultValue)
      return matches[0].defaultValue;
    return 'ERROR'; // We dont actually use this value, just needs to be non-empty
  }

  if (fieldId === TICKET_BUYER_SURNAME_FIELD_ID) {
    const matches = fields.filter(
      (f) => f.id === TICKET_BUYER_SURNAME_FIELD_ID,
    );
    if (matches && matches.length > 0 && matches[0].defaultValue)
      return matches[0].defaultValue;
    return 'ERROR'; // We dont actually use this value, just needs to be non-empty
  }

  return undefined;
};

export const userIsBookingForThemselves = (values, friendFields) => {
  const value = values[IS_PARTICIPANT_FIELD_ID];
  return !friendFields && value && value === "I'm booking for myself";
};

export const shouldHideField = (fieldId, values, friendFields) => {
  if (!friendFields) {
    // When not showing friend fields, default to hiding the
    // participant fields unless the user isn't booking for themselves
    if (BUYER_NOT_PARTICIPANT_FIELDS.includes(fieldId)) {
      return userIsBookingForThemselves(values, friendFields);
    }
  } else {
    // If we are showing friends fields, we hide the buyer fields and
    // the switcher
    const rawFieldId = fieldId.split('_')[1];
    return TICKET_BUYER_FIELDS.includes(rawFieldId);
  }
};

export const matchingParticipantField = (fieldId, values, friendFields) => {
  if (userIsBookingForThemselves(values, friendFields)) {
    if (fieldId === TICKET_BUYER_FIRST_NAME_FIELD_ID) {
      return PARTICIPANT_FIRST_NAME_FIELD_ID;
    }

    if (fieldId === TICKET_BUYER_SURNAME_FIELD_ID) {
      return PARTICIPANT_SURNAME_FIELD_ID;
    }
  }

  return undefined;
};

export const updateParticipantFieldsWithBuyerDetails = (values) => {
  const firstName = values[TICKET_BUYER_FIRST_NAME_FIELD_ID];
  const surname = values[TICKET_BUYER_SURNAME_FIELD_ID];

  const newValues = {};
  Object.assign(newValues, values);

  newValues[PARTICIPANT_FIRST_NAME_FIELD_ID] = firstName;
  newValues[PARTICIPANT_SURNAME_FIELD_ID] = surname;

  return newValues;
};

export const getUserContactInfo = (values) => {
  const firstName = values[PARTICIPANT_FIRST_NAME_FIELD_ID];
  const lastName = values[PARTICIPANT_SURNAME_FIELD_ID];
  const email =
    values[PARTICIPANT_EMAIL_FIELD_ID] &&
    values[PARTICIPANT_EMAIL_FIELD_ID].email;
  const phone = values[PARTICIPANT_PHONE_FIELD_ID];

  return { firstName, lastName, email, phone };
};

// TODO: put this in a sensible place and perhaps replace the isMobileOrTablet
export function isDesktop() {
  if (__SERVER__) return false;
  const { matches } = window.matchMedia(`(min-width: ${breakpointLGInt}px)`);
  return matches;
}
export const isDesktopMemo = memoize(isDesktop);

const autofillMapping = new Map([
  ['First name', 'given-name'],
  ['Surname', 'family-name'],
  ['Email', 'email'],
  ['Gender', 'sex'],
  ['Date of birth Day', 'bday-day'],
  ['Date of birth Month', 'bday-month'],
  ['Date of birth Year', 'bday-year'],
  ['Line 1', 'address-line1'],
  ['Line 2', 'address-line2'],
  ['City', 'address-level2'],
  ['Country', 'address-level1'],
  ['Postcode', 'postal-code'],
  ['familyName', 'family-name'],
  ['givenName', 'given-name'],
  ['emailAddress', 'email'],
  ['gender', 'sex'],
  // TODO Plan to add more and or seperate when have completed Virtual Race feature.
]);

export function getAutofillString(name, discriminator = 0) {
  if (discriminator !== 0) return 'off';
  if (autofillMapping.has(name)) return autofillMapping.get(name);

  return `${name?.replace?.(/\s/g, '-')?.toLowerCase?.()}`;
}

export function getUniqueId(id, discriminator) {
  return [id, discriminator].filter(Boolean).join('-');
}

export const isDonationEnabled = (event) => {
  return event?.charityDonation?.enabled === true;
};
export const shouldShowCharityCheckboxes = ({
  countryShort,
  event,
  organiserId = 0,
}) => {
  const eventId = event?.id;
  if (BLACKLISTED_EVENT_IDS.includes(eventId)) return false;
  if (BLACKLISTED_EO_IDS.includes(organiserId)) return false;
  if (countryShort !== 'GB') return false;
  if (isDonationEnabled(event)) return false;
  return true;
};

export const shouldShowVELogingWall = (virtualEventDetails = {}) => {
  return (
    virtualEventDetails?.isVirtual === true &&
    virtualEventDetails?.enableVirtualResultSubmission === true
  );
};
export const shouldShowFriendsBooking = (event) => {
  /*
   conditional expression can be used here
   */
  return true;
};
